import { CircularProgress, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import {
  FC,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthenticationContext,
  LanguageCode,
  notificationsActions,
  useAppDispatch,
  useUpdateAssociate,
} from '@vs-online-only-app/state';
import SmallButton from '../../SmallButton';
import LanguageSelector, { getDefaultLanguages } from '../../LanguagesSelector';

const areArraysEqual = function (arr1: string[], arr2: string[]) {
  const arr1Copy = [...arr1];
  const arr2Copy = [...arr2];
  arr1Copy.sort();
  arr2Copy.sort();
  return (
    arr1.length === arr2Copy.length &&
    !arr1Copy.some((a, i) => a !== arr2Copy[i])
  );
};

type Props = {
  onFocus: () => void;
  abort: boolean;
};

const EditProfileLanguages: FC<Props> = ({ onFocus, abort }) => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthenticationContext);
  const [isEditModeOn, setEditModeOn] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [languages, setLanguages] = useState<LanguageCode[]>(
    getDefaultLanguages(user),
  );
  const updateAssociate = useUpdateAssociate();

  useEffect(() => {
    if (!abort || !user) return;
    setEditModeOn(false);
    setLanguages(getDefaultLanguages(user));
    setErrorMessage('');
  }, [abort, user]);

  const handleFieldFocus = useCallback(() => {
    setEditModeOn(true);
    onFocus();
  }, [onFocus]);

  const handleFieldChange = useCallback((newValue: LanguageCode[]) => {
    if (!newValue) {
      setErrorMessage('missing value');
    } else {
      setErrorMessage('');
    }
    setLanguages(newValue);
  }, []);

  const handleFieldBlur = useCallback(() => {
    if (!user) return;

    const spokenLanguages = languages.map(({ languageCode }) => languageCode);
    if (areArraysEqual(spokenLanguages, user.spokenLanguages)) {
      setEditModeOn(false);
    }
  }, [languages, user]);

  const onSave = useCallback(async () => {
    if (!languages.length || !user) return;

    const spokenLanguages = languages.map(({ languageCode }) => languageCode);
    if (areArraysEqual(spokenLanguages, user.spokenLanguages)) return;

    setIsBusy(true);
    try {
      await updateAssociate.mutateAsync({ spokenLanguages });
      setEditModeOn(false);
      dispatch(
        notificationsActions.setCurrentNotification({
          message: 'Languages has been updated.',
          severity: 'success',
        }),
      );
    } catch (e) {
      if (e instanceof AxiosError) {
        const err = (e as AxiosError<{ message: string }>).response?.data
          ?.message;
        dispatch(
          notificationsActions.setCurrentNotification({
            message:
              typeof err === 'string' ? err : `Could not update Languages`,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          notificationsActions.setCurrentNotification({
            message: (e as Error).message,
            severity: 'error',
          }),
        );
      }
    }
    setIsBusy(false);
  }, [dispatch, languages, updateAssociate, user]);

  const onCancel = useCallback(() => {
    if (!user) return;
    setLanguages(getDefaultLanguages(user));

    setEditModeOn(false);
    setErrorMessage('');
  }, [user]);

  const isSaveButtonDisabled = useMemo(() => {
    if (!user) return true;
    if (!languages.length) {
      setErrorMessage('Required');
      return true;
    }
    const spokenLanguages = languages.map(({ languageCode }) => languageCode);
    return areArraysEqual(user.spokenLanguages, spokenLanguages);
  }, [user, languages]);

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave();
  };

  return (
    <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
      <Stack direction="column" gap={2}>
        <LanguageSelector
          label={`Languages${errorMessage ? ` - ${errorMessage}` : ''}`}
          error={!!errorMessage}
          disabled={isBusy}
          onChange={handleFieldChange}
          onFocus={handleFieldFocus}
          onBlur={handleFieldBlur}
          languages={languages}
        />

        {isEditModeOn && (
          <Stack direction="row" justifyContent="right">
            <SmallButton disabled={isBusy} onClick={onCancel}>
              Cancel
            </SmallButton>
            <SmallButton
              disabled={!!errorMessage || isSaveButtonDisabled || isBusy}
              variant="contained"
              onClick={onSave}
            >
              {isBusy ? <CircularProgress color="inherit" size={20} /> : 'Save'}
            </SmallButton>
          </Stack>
        )}
      </Stack>
    </form>
  );
};

export default EditProfileLanguages;
